import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { isMobile, isTablet } from "react-device-detect";
import { useLocation } from "react-router-dom";

interface SpecialRightsContextType {
  hasSpecialRights: boolean;
  grantSpecialRights: () => void;
  isLoading: boolean;
}

const SpecialRightsContext = createContext<SpecialRightsContextType | null>(
  null
);

export const useSpecialRights = (): SpecialRightsContextType => {
  const context = useContext(SpecialRightsContext);
  if (!context) {
    throw new Error("SpecialRightsContext not found");
  }
  return context;
};

interface Props {
  children: ReactNode;
}

export const SpecialRightsProvider: React.FC<Props> = ({ children }) => {
  const [hasSpecialRights, setHasSpecialRights] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const evaluateRights = () => {
      if (process.env.REACT_APP_DEV_OVERRIDE === "true") {
        setHasSpecialRights(true);
        setIsLoading(false);
        return;
      }

      const params = new URLSearchParams(location.search);
      const key = params.get("qrcode");

      if (key && key === process.env.REACT_APP_QRCODE) {
        setHasSpecialRights(true);
      }

      if (process.env.REACT_APP_ALLOW_MOBILE_AND_TABLET === "true") {
        if (isMobile || isTablet) {
          setHasSpecialRights(true);
        }
      }

      setIsLoading(false);
    };

    evaluateRights();
  }, [location.search]);

  const grantSpecialRights = () => setHasSpecialRights(true);

  const value = useMemo(
    () => ({ hasSpecialRights, grantSpecialRights, isLoading }),
    [hasSpecialRights, isLoading]
  );

  return (
    <SpecialRightsContext.Provider value={value}>
      {children}
    </SpecialRightsContext.Provider>
  );
};
